import { isValidElement, useState } from "react"

import { Flex, Typography } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import Icon from "../../Icon"
import { buildSearchWithFlag } from "../../Utils/queryFlags"
import ButtonComponent from "../Button"
import VideoPlayerModal from "../VideoPlayerModal/VideoPlayerModal"
import { useTitleSectionAction } from "./TitleSectionContext"

import "./TitleSection.less"

const { Title, Text } = Typography

const TitleSection = ({
  title,
  description,
  icon,
  isAdmin,
  businessAccountUrl,
  buttonText,
  videoUrl,
  businessSetting = false,
  stacked = false,
  style = {},
  className,
  queryParam,
  queryButtonText = "View logs",
  children
}) => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const externalDemoMode = useExternalDemoMode()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { action } = useTitleSectionAction()
  const { isMobile } = useSelector((state) => state.nav)
  const showMobileTutorialButton = buttonText && isMobile && !action

  const handleOpenQueryModal = () => {
    navigate({
      pathname,
      search: buildSearchWithFlag(search, queryParam, true)
    })
  }

  const handleAction = () => {
    return externalDemoMode ? null : setVisible(true)
  }

  const tutorialButton = buttonText && !isAdmin && (
    <>
      {isMobile ? (
        <ButtonComponent
          height="40"
          width="40"
          icon={<Icon type={"mobile_video_course"} />}
          onClick={handleAction}
          disabled={externalDemoMode}
        />
      ) : (
        <a
          className="title-section-tutorial-link"
          aria-disabled={externalDemoMode}
          onClick={handleAction}
        >
          <Icon type={"watch_tutorial"} />
          {t(buttonText)}
        </a>
      )}
    </>
  )

  return (
    <>
      <Flex
        align={isMobile ? "flex-start" : "center"}
        justify="space-between"
        vertical={isMobile}
        gap={isMobile ? 4 : 0}
        className={cls("title-section", { "is-admin-view": isAdmin }, className)}
        style={{ ...style }}
      >
        {isMobile ? (
          <>
            <Flex align={"center"} justify="space-between" gap={12} style={{ width: "100%" }}>
              <Flex align={"center"} gap={12}>
                {icon && (
                  <Flex align="center" justify="center" className="title-section-icon">
                    <Icon type={icon} />
                  </Flex>
                )}
                {isValidElement(title) ? (
                  title
                ) : (
                  <Title
                    level={5}
                    style={{
                      textAlign: "left",
                      marginBottom: 0,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {t(title)}
                  </Title>
                )}
              </Flex>
              <Flex align={"center"} gap={12}>
                {tutorialButton}
                {action}
                {queryParam && (
                  <button
                    className="descriptor-logs-trigger title-section-logs-trigger"
                    onClick={handleOpenQueryModal}
                    type="button"
                  >
                    <Icon type="info_bubble_empty" className="descriptor-logs-trigger-icon" />
                    <Text type="secondary" strong className="descriptor-logs-trigger-text">
                      {t(queryButtonText)}
                    </Text>
                  </button>
                )}
              </Flex>
            </Flex>
            <Text type="secondary">
              {isValidElement(description) ? description : t(description)}
            </Text>
            {children && (
              <Flex
                align={"center"}
                gap={12}
                className={"title-section-actions"}
                wrap
                id="page-header-actions"
              >
                {children}
              </Flex>
            )}
          </>
        ) : (
          <>
            <Flex align={"center"} gap={12}>
              {icon && (
                <Flex align="center" justify="center" className="title-section-icon">
                  <Icon type={icon} />
                </Flex>
              )}
              <Flex
                align={stacked ? "flex-start" : "center"}
                gap={stacked ? 4 : 8}
                vertical={stacked}
              >
                {isValidElement(title) ? (
                  title
                ) : (
                  <Title
                    level={5}
                    style={{
                      textAlign: "left",
                      marginBottom: 0,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {t(title)}
                  </Title>
                )}
                <Text type="secondary">
                  {isValidElement(description) ? description : t(description)}
                </Text>
              </Flex>
            </Flex>
            <Flex
              align={"center"}
              gap={12}
              className={"title-section-actions"}
              wrap
              id="page-header-actions"
            >
              {queryParam && (
                <button
                  className="descriptor-logs-trigger title-section-logs-trigger"
                  onClick={handleOpenQueryModal}
                  type="button"
                >
                  <Icon type="info_bubble_empty" className="descriptor-logs-trigger-icon" />
                  <Text type="secondary" strong>
                    {t(queryButtonText)}
                  </Text>
                </button>
              )}

              {tutorialButton}
              {action}
              {children}
            </Flex>
          </>
        )}
      </Flex>
      <VideoPlayerModal videoUrl={videoUrl} isOpened={visible} close={() => setVisible(false)} />
    </>
  )
}

TitleSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.string,
  buttonText: PropTypes.string,
  videoUrl: PropTypes.string
}

export default TitleSection
